import SubRouteAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/routing/sub-route/sub-route.entity';
import VehicleAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/transport/vehicle/vehicle.entity';
import { territoryIdOf } from 'app/modules/components/routing/sub-routes/route-optimization/delivery-territory';
import { isDeliverySubRoute } from 'app/modules/components/routing/sub-routes/route-optimization/plan-partitioning';

/**
 * Catálogos de la corrida —territorios y camiones— derivados del **maestro de
 * sub-ruta**, el mismo que consume la pantalla `master/routing/sub-route`.
 *
 * Por qué existe: estos dos catálogos se pedían a `GET sub-route/territories` y
 * `GET sub-route/vehicles`, que viven en OTRO ambiente (`API_ROUTING_PLANNING_URL`
 * apuntaba a QAS mientras el maestro y los pedidos salían de sandbox). Medido
 * contra el ambiente de desarrollo, los dos responden `400 "Rutas no
 * encontradas."` para las oficinas que existen en el maestro, así que el
 * selector de territorios quedaba vacío y el de camiones caía al catálogo
 * COMPLETO de la compañía — 7 camiones sin oficina ni territorio, ninguno de
 * ellos asignado a una ruta. Un plan calculado con esa flota no es el plan de
 * nadie.
 *
 * El maestro ya tiene las tres relaciones que hacen falta, y en un solo
 * documento por sub-ruta:
 *
 *     sub-route (ENTREGA) ──► routes[]   el territorio que reparte
 *                        └──► vehicle    el camión asignado
 *
 * Así que el catálogo se DERIVA de él: una sola consulta, la misma que ya hacía
 * `SubRouteService.findForPlanning`, contra la misma API que la pantalla de
 * sub-rutas. No hay un segundo ambiente que pueda desincronizarse.
 *
 * Módulo puro: entran las sub-rutas del maestro, salen los catálogos. Sin HTTP.
 */

/**
 * Territorio ofrecido en el filtro de la corrida.
 *
 * Vivía en `SubRouteService` como tipo de retorno del endpoint eliminado; se
 * mudó acá con el resto de la derivación para que el productor y el tipo de lo
 * producido queden juntos.
 */
export interface PlanningTerritory {
  _id?: string;
  id: number;
  name: string;
  externalKeys?: { code?: string };
}

/** Camión asignado en el maestro, con los territorios que sirve. */
export interface AssignedTruck {
  vehicle: VehicleAttributes;
  /** Ids de territorio que sirve, vía las sub-rutas de ENTREGA que lo declaran. */
  territoryIds: number[];
  /** Nombres de esos territorios, para rotular la opción. */
  territoryNames: string[];
  /** `_id` de las sub-rutas de ENTREGA que lo declaran. */
  subRouteIds: string[];
}

/** Referencia a territorio tal como la trae el maestro. */
type RouteRef = {
  _id?: unknown;
  id?: unknown;
  name?: unknown;
  externalKeys?: { code?: string };
};

/** Normaliza un id de Mongo (string u ObjectId) a string, o `undefined`. */
function idOf(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  const id = String(value);
  return id.length ? id : undefined;
}

function routesOf(subRoute: SubRouteAttributes): RouteRef[] {
  return ((subRoute as unknown as { routes?: RouteRef[] }).routes ?? []).filter((r) => !!r);
}

function vehicleOf(subRoute: SubRouteAttributes): VehicleAttributes | undefined {
  const vehicle = (subRoute as unknown as { vehicle?: VehicleAttributes | null }).vehicle;
  return vehicle ? vehicle : undefined;
}

/**
 * Sub-rutas que REPARTEN, las únicas que aportan a estos catálogos.
 *
 * El predicado es el de `plan-partitioning` —importado, no copiado—: una regla de
 * negocio con dos definiciones divergiría sin dar error. Deja fuera PREVENTA, que
 * es de donde salen los territorios en bloque: medido en el maestro, las 39
 * sub-rutas de ENTREGA declaran **exactamente un** territorio cada una, mientras
 * las de PREVENTA llegan a declarar 29. Sin este filtro el selector de
 * territorios se llena de zonas que ningún camión reparte, y el de camiones
 * podría ofrecer el vehículo de un vendedor.
 */
function deliverySubRoutes(subRoutes: readonly SubRouteAttributes[]): SubRouteAttributes[] {
  return (subRoutes ?? []).filter((sr) => !!sr && isDeliverySubRoute(sr));
}

/** Una sub-ruta de ENTREGA y el camión que declara: la unidad de flota. */
export interface FleetUnit {
  subRoute: SubRouteAttributes;
  vehicle: VehicleAttributes;
}

/**
 * Un territorio y TODOS los camiones asignados a sus rutas de entrega.
 *
 * Es la unidad de planificación: un `POST /plan` por territorio, con estos
 * camiones. Nunca sale con `units` vacío — un territorio sin flota no es una
 * unidad de planificación, es una brecha.
 */
export interface TerritoryFleet {
  territoryId: number;
  territoryName: string;
  units: FleetUnit[];
}

/**
 * Un camión asignado a rutas de territorios distintos DENTRO del alcance: no se
 * puede saber cuál de sus rutas hace hoy, así que la corrida no corre.
 */
export interface CrossTerritoryConflict {
  vehicleId: string;
  /** Placa (o código) para nombrarlo en el aviso. */
  label: string;
  territoryIds: number[];
  territoryNames: string[];
  /**
   * Rutas que lo declaran. Es lo que se nombra en el mensaje: el operador
   * reconoce `ENTREGA 800`, que es lo que edita en el maestro; `TEGUCIGALPA 800`
   * es el territorio y en esa pantalla no es lo que ve.
   */
  subRouteNames: string[];
}

/** Cómo se nombra un vehículo en un aviso: la placa, o el código si no hay. */
function plateOf(vehicle: VehicleAttributes): string {
  const v = vehicle as unknown as { plate?: string; code?: string; name?: string };
  return (v.plate || v.code || v.name || '').trim();
}

/** Territorios (id → nombre) que declara una sub-ruta, ya normalizados. */
function territoriesOf(subRoute: SubRouteAttributes): Map<number, string> {
  const out = new Map<number, string>();
  for (const route of routesOf(subRoute)) {
    const id = territoryIdOf(route.id);
    if (id === undefined || out.has(id)) continue;
    out.set(id, route.name === undefined || route.name === null ? '' : String(route.name));
  }
  return out;
}

/**
 * Camiones que varias rutas del alcance se **disputan**.
 *
 * No es un error del maestro: el maestro dice qué rutas *puede* hacer el camión, no
 * cuál hace hoy. Lo que falta es el dato del día, y lo resuelve
 * `resolveDuplicateVehicles` asignándolo donde rinde más.
 *
 * Antes esto se llamaba conflicto y tenía dos respuestas, las dos malas: excluir el
 * camión —el plan salía y le faltaba un camión real, sin explicación en pantalla— o
 * bloquear la corrida, que no daba ningún plan ni siquiera a los territorios que
 * estaban impecables.
 *
 * El alcance es parte de la definición: un camión no está disputado en abstracto, lo
 * está en una corrida que incluye más de uno de sus territorios. Medido en el maestro
 * real, `HAU3385` llegó a estar en los territorios 61 y 70 de la oficina 4 y en el 99
 * de la oficina 5 — evaluarlo contra todo el maestro haría competir territorios que
 * ni participan de la corrida.
 *
 * @param territoryIds territorios del alcance; vacío = todos.
 */
export function crossTerritoryConflicts(
  subRoutes: readonly SubRouteAttributes[],
  territoryIds: readonly number[] = [],
): CrossTerritoryConflict[] {
  const scope = new Set(territoryIds);
  const inScope = (id: number): boolean => scope.size === 0 || scope.has(id);

  const byVehicle = new Map<
    string,
    { vehicle: VehicleAttributes; territories: Map<number, string>; subRoutes: string[] }
  >();
  for (const subRoute of deliverySubRoutes(subRoutes)) {
    const vehicle = vehicleOf(subRoute);
    const vehicleId = idOf(vehicle?._id);
    if (!vehicle || !vehicleId) continue;
    const mine = [...territoriesOf(subRoute)].filter(([id]) => inScope(id));
    // Una sub-ruta cuyos territorios están todos fuera del alcance no aporta al
    // conflicto: su camión no compite por nada en ESTA corrida.
    if (!mine.length) continue;
    const entry =
      byVehicle.get(vehicleId) ??
      { vehicle, territories: new Map<number, string>(), subRoutes: [] };
    for (const [id, name] of mine) {
      if (!entry.territories.has(id)) entry.territories.set(id, name);
    }
    const subRouteName = (subRoute as unknown as { name?: unknown }).name;
    const label = subRouteName === undefined || subRouteName === null ? '' : String(subRouteName);
    if (label && !entry.subRoutes.includes(label)) entry.subRoutes.push(label);
    byVehicle.set(vehicleId, entry);
  }

  const conflicts: CrossTerritoryConflict[] = [];
  for (const [vehicleId, { vehicle, territories, subRoutes: names }] of byVehicle) {
    if (territories.size <= 1) continue;
    const ids = [...territories.keys()].sort((a, b) => a - b);
    conflicts.push({
      vehicleId,
      label: plateOf(vehicle),
      territoryIds: ids,
      territoryNames: ids.map((id) => territories.get(id) ?? String(id)),
      subRouteNames: names,
    });
  }
  return conflicts;
}

/** A qué territorio se asignó un camión disputado, y de cuáles se lo quitó. */
export interface DuplicateResolution {
  vehicleId: string;
  /** Placa, para nombrarlo. */
  label: string;
  territoryId: number;
  territoryName: string;
  /** Pedidos del territorio que se lo queda. */
  orders: number;
  /** Territorios que lo pierden, con lo que dejan de poder entregar. */
  lost: Array<{ territoryId: number; territoryName: string; orders: number }>;
}

/** Un territorio candidato con su demanda, para decidir la disputa. */
export interface TerritoryDemand {
  orders: number;
  weightLb: number;
}

/**
 * A qué territorio va cada camión disputado.
 *
 * El criterio es **más pedidos**: el camión no puede estar en dos lados, así que
 * alguno de los territorios se queda sin él de todos modos — asignarlo donde rinde
 * más produce un plan útil, y el que pierde entra a la brecha de flota con su
 * dimensionamiento, que es la respuesta accionable ("acá faltan N camiones").
 *
 * El desempate es explícito y determinista: pedidos → peso → id de territorio más
 * bajo. Sin declararlo, el resultado dependería del orden en que el maestro devuelve
 * las filas y el mismo alcance daría planes distintos entre corridas.
 *
 * Un territorio en disputa sin demanda conocida cuenta como cero: no compite contra
 * uno que sí tiene pedidos, que es lo correcto.
 *
 * Módulo puro: entran las disputas y la demanda por territorio, sale la decisión.
 */
export function resolveDuplicateVehicles(
  conflicts: readonly CrossTerritoryConflict[],
  demandByTerritory: ReadonlyMap<number, TerritoryDemand>,
): DuplicateResolution[] {
  const ZERO: TerritoryDemand = { orders: 0, weightLb: 0 };
  const resolutions: DuplicateResolution[] = [];

  for (const conflict of conflicts ?? []) {
    const nameOf = new Map(
      conflict.territoryIds.map((id, i) => [id, conflict.territoryNames[i] ?? String(id)]),
    );
    // Orden de decisión, explícito: más pedidos, más peso, id más bajo.
    const ranked = [...conflict.territoryIds].sort((a, b) => {
      const da = demandByTerritory.get(a) ?? ZERO;
      const db = demandByTerritory.get(b) ?? ZERO;
      return db.orders - da.orders || db.weightLb - da.weightLb || a - b;
    });
    const [winner, ...losers] = ranked;
    if (winner === undefined) continue;

    resolutions.push({
      vehicleId: conflict.vehicleId,
      label: conflict.label,
      territoryId: winner,
      territoryName: nameOf.get(winner) ?? String(winner),
      orders: (demandByTerritory.get(winner) ?? ZERO).orders,
      lost: losers.map((id) => ({
        territoryId: id,
        territoryName: nameOf.get(id) ?? String(id),
        orders: (demandByTerritory.get(id) ?? ZERO).orders,
      })),
    });
  }
  return resolutions;
}

export interface FleetByTerritoryOptions {
  /** Camiones que el usuario excluyó de la corrida (taller, préstamo). */
  excludedVehicleIds?: readonly string[];
  /**
   * Territorios del alcance; vacío = todos.
   *
   * **Tiene que ser el MISMO alcance con el que se evaluó `crossTerritoryConflicts`.**
   * Si la disputa se mira acotada y la flota no, queda un hueco por el que el camión
   * se va en dos peticiones: la detección diría "en este alcance no compite" mientras
   * la flota lo pone igual en los dos territorios.
   */
  territoryIds?: readonly number[];
  /**
   * Territorio ganador de cada camión disputado (`resolveDuplicateVehicles`). El
   * camión entra SOLO ahí; en los demás territorios se lo saca.
   */
  assignedTerritoryByVehicle?: ReadonlyMap<string, number>;
}

/**
 * La flota de la corrida, agrupada por TERRITORIO.
 *
 * Es el reemplazo de "un camión por grupo de pedidos". Cada territorio con flota
 * es un `POST /plan` con sus camiones; los territorios que no aparecen acá no se
 * planifican, y sus pedidos van a la brecha con motivo.
 *
 * Deja afuera:
 * 1. los camiones que el usuario excluyó de la corrida (taller, préstamo);
 * 2. los territorios que quedan sin ningún camión — un territorio sin flota no es
 *    una unidad de planificación con cero camiones, es una brecha, y devolverlo
 *    con `units: []` invitaría a emitir una petición vacía.
 *
 * Un camión **disputado** entra solo en el territorio que ganó
 * (`assignedTerritoryByVehicle`). Sin esa decisión entraría en todos los suyos y cada
 * petición le daría la jornada y la capacidad completas — el plan diría que sale y el
 * camión no vuelve.
 *
 * La unidad conserva su `subRoute` porque de ahí sale el turno, las ventanas y el
 * multi-viaje de ESE camión: dos camiones del mismo territorio pueden tener
 * jornadas distintas, y `trucks[]` del contrato admite un `shift` por camión.
 */
export function fleetByTerritory(
  subRoutes: readonly SubRouteAttributes[],
  options: FleetByTerritoryOptions = {},
): TerritoryFleet[] {
  const excluded = new Set(options.excludedVehicleIds ?? []);
  const scope = new Set(options.territoryIds ?? []);
  const assigned = options.assignedTerritoryByVehicle;

  const byTerritory = new Map<number, TerritoryFleet>();
  for (const subRoute of deliverySubRoutes(subRoutes)) {
    const vehicle = vehicleOf(subRoute);
    const vehicleId = idOf(vehicle?._id);
    if (!vehicle || !vehicleId || excluded.has(vehicleId)) continue;
    // Camión disputado: solo en su territorio ganador.
    const won = assigned?.get(vehicleId);
    for (const [territoryId, territoryName] of territoriesOf(subRoute)) {
      if (scope.size && !scope.has(territoryId)) continue;
      if (won !== undefined && territoryId !== won) continue;
      const entry =
        byTerritory.get(territoryId) ?? { territoryId, territoryName, units: [] };
      // Una sub-ruta aporta UNA unidad a su territorio. La misma placa declarada
      // por dos sub-rutas del mismo territorio son dos unidades a propósito: son
      // el mismo camión y `resolveTerritoryFleet` las funde por `vehicle._id`.
      entry.units.push({ subRoute, vehicle });
      byTerritory.set(territoryId, entry);
    }
  }

  return [...byTerritory.values()]
    .filter((t) => t.units.length > 0)
    .sort((a, b) => a.territoryName.localeCompare(b.territoryName));
}

/**
 * Territorios de reparto de las sub-rutas dadas, sin repetir.
 *
 * Se ordenan por nombre porque el resultado ES el orden del combo: el del maestro
 * es el de inserción y hace que el mismo catálogo se vea distinto entre corridas.
 * Un territorio sin id usable no entra — sin id no se puede filtrar por él.
 */
export function territoriesFromSubRoutes(
  subRoutes: readonly SubRouteAttributes[],
): PlanningTerritory[] {
  const byId = new Map<number, PlanningTerritory>();
  for (const subRoute of deliverySubRoutes(subRoutes)) {
    for (const route of routesOf(subRoute)) {
      const id = territoryIdOf(route.id);
      if (id === undefined || byId.has(id)) continue;
      byId.set(id, {
        _id: idOf(route._id),
        id,
        name: route.name === undefined || route.name === null ? '' : String(route.name),
        externalKeys: route.externalKeys,
      });
    }
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Camiones ASIGNADOS en el maestro, con los territorios que sirven.
 *
 * Un camión puede estar declarado por varias sub-rutas —está en los datos: en el
 * ambiente de pruebas una placa aparece en tres, y hasta en dos oficinas
 * distintas—, así que se agrupa por `vehicle._id` y se acumulan sus territorios.
 * Eso hace visible en la propia opción del combo cuando un camión rompe la regla
 * de "un camión, un territorio", en vez de esconderlo detrás de la primera
 * sub-ruta que lo declaró.
 *
 * NO se filtra por capacidad. Un camión asignado con `grossWeight` en 0 sigue
 * siendo el camión que sale: ocultarlo diría que la ruta no tiene camión, que es
 * falso. Su grupo cae a la capacidad de respaldo y `resolveGroupFleet` ya lo
 * declara con `fleetFromMaster: false`, que es donde el operador tiene que verlo.
 *
 * @param territoryIds acota a los camiones que sirven estos territorios; vacío =
 *   todos, el mismo contrato que tenía el endpoint que reemplaza.
 */
export function assignedTrucksFromSubRoutes(
  subRoutes: readonly SubRouteAttributes[],
  territoryIds: readonly number[] = [],
): AssignedTruck[] {
  const scope = new Set(territoryIds);
  const byVehicle = new Map<string, AssignedTruck>();

  for (const subRoute of deliverySubRoutes(subRoutes)) {
    const vehicle = vehicleOf(subRoute);
    const vehicleId = idOf(vehicle?._id);
    if (!vehicle || !vehicleId) continue;

    const ids = routesOf(subRoute)
      .map((r) => territoryIdOf(r.id))
      .filter((id): id is number => id !== undefined);
    // Con alcance declarado, el camión entra solo si sirve alguno de esos
    // territorios. Una sub-ruta sin territorio usable no puede satisfacer un
    // alcance, pero sin alcance su camión sigue siendo un camión asignado.
    if (scope.size && !ids.some((id) => scope.has(id))) continue;

    const entry = byVehicle.get(vehicleId) ?? {
      vehicle,
      territoryIds: [],
      territoryNames: [],
      subRouteIds: [],
    };
    for (const route of routesOf(subRoute)) {
      const id = territoryIdOf(route.id);
      if (id === undefined || entry.territoryIds.includes(id)) continue;
      entry.territoryIds.push(id);
      const name = route.name === undefined || route.name === null ? '' : String(route.name);
      if (name && !entry.territoryNames.includes(name)) entry.territoryNames.push(name);
    }
    const subRouteId = idOf((subRoute as unknown as { _id?: unknown })._id);
    if (subRouteId && !entry.subRouteIds.includes(subRouteId)) entry.subRouteIds.push(subRouteId);
    byVehicle.set(vehicleId, entry);
  }

  return [...byVehicle.values()].sort((a, b) => vehicleLabel(a.vehicle).localeCompare(vehicleLabel(b.vehicle)));
}

/** Con qué texto se ordena el combo: el nombre, y la placa para desempatar. */
function vehicleLabel(vehicle: VehicleAttributes): string {
  const v = vehicle as unknown as { name?: string; plate?: string; code?: string };
  return `${v.name ?? ''} ${v.plate ?? v.code ?? ''}`.trim();
}
